import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { Target, Cpu, Microscope, Users, Zap, HeartHandshake } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1579154204601-01588f351e67?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function About() {
  const { t } = useLang();
  const features = [
    { icon: Target, title: "Precision", text: "Calibrated instruments for reliable, repeatable results." },
    { icon: Cpu, title: "Technology", text: "Fully automated analysers and digital reporting." },
    { icon: Microscope, title: "Modern Equipment", text: "State-of-the-art diagnostic machines." },
    { icon: Users, title: "Qualified Staff", text: "Experienced, certified lab technicians." },
    { icon: Zap, title: "Fast Results", text: "Same day reports delivered digitally." },
    { icon: HeartHandshake, title: "Personal Care", text: "Patient-first, compassionate service." },
  ];
  return (
    <section id="about" className="py-20 md:py-28" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="lg:sticky lg:top-28">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-turq-dark">{t("about_label")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-3 tracking-tight">{t("about_title")}</h2>
          <p className="text-secondary mt-5 text-lg leading-relaxed">
            DOABA Diagnostics Laboratory in Phillaur combines advanced technology with experienced professionals to deliver accurate, affordable and timely diagnostic services. From routine blood work to specialised tests, we are committed to your health and peace of mind.
          </p>
          <div className="mt-6 rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-25px_rgba(10,37,64,0.4)]">
            <img src={ABOUT_IMG} alt="DOABA laboratory technician at work" className="w-full h-[320px] object-cover" />
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-transform duration-300" data-testid={`about-feature-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center text-turq-light"><f.icon className="w-6 h-6" strokeWidth={1.6} /></div>
              <h3 className="font-heading font-semibold text-navy mt-4 text-lg">{f.title}</h3>
              <p className="text-secondary text-sm mt-2 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
